<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import Chart from "chart.js/auto";
import STATUS_TONE_MAP from "@/Constans/StatusToneMap";

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

const canvasRef = ref(null);
let chartInstance = null;

const chartData = computed(() => {
  const items = Array.isArray(props.items) ? props.items : [];

  return {
    labels: items.map((item) => item?.hour || "-"),
    datasets: [
      {
        label: "Pendientes",
        data: items.map((item) => normalizeNumber(item?.pending)),
        borderColor: STATUS_TONE_MAP.en_curso.color,
        backgroundColor: `${STATUS_TONE_MAP.en_curso.border}26`,
      },
      {
        label: "Completadas",
        data: items.map((item) => normalizeNumber(item?.completed)),
        borderColor: STATUS_TONE_MAP.finalizado.color,
        backgroundColor: `${STATUS_TONE_MAP.finalizado.border}26`,
      },
      {
        label: "Incidencias",
        data: items.map((item) => normalizeNumber(item?.incidence)),
        borderColor: STATUS_TONE_MAP.incidencia.color,
        backgroundColor: `${STATUS_TONE_MAP.incidencia.border}26`,
      },
    ],
  };
});

const hasData = computed(() => chartData.value.labels.length > 0);

onMounted(() => {
  renderChart();
});

watch(
  chartData,
  () => {
    renderChart();
  },
  { deep: true }
);

onBeforeUnmount(() => {
  destroyChart();
});

function normalizeNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.round(parsed));
}

async function renderChart() {
  await nextTick();

  if (!hasData.value || !canvasRef.value) {
    destroyChart();
    return;
  }

  destroyChart();

  const context = canvasRef.value.getContext("2d");

  chartInstance = new Chart(context, {
    type: "line",
    data: {
      labels: chartData.value.labels,
      datasets: chartData.value.datasets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        borderColor: dataset.borderColor,
        backgroundColor: dataset.backgroundColor,
        fill: true,
        tension: 0.35,
        borderWidth: 3,
        pointRadius: 3,
        pointHoverRadius: 5,
      })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: "index",
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
          align: "start",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: "circle",
            color: "#4b5563",
            font: {
              size: 11,
              weight: "500",
            },
          },
        },
      },
      layout: {
        padding: {
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            color: "#6b7280",
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            drawBorder: false,
            color: "rgba(103, 116, 142, 0.12)",
          },
          ticks: {
            precision: 0,
            color: "#6b7280",
          },
        },
      },
    },
  });
}

function destroyChart() {
  if (!chartInstance) return;
  chartInstance.destroy();
  chartInstance = null;
}
</script>

<template>
  <div class="card h-100 hourly-progress-card">
    <div class="card-header pb-0">
      <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-0">Progreso paradas</p>
    </div>
    <div class="card-body hourly-progress-card__body">
      <div v-if="hasData" class="progress-chart__canvas-wrapper">
        <canvas ref="canvasRef" class="progress-chart__canvas"></canvas>
      </div>
      <div v-else class="text-sm text-secondary">
        Sin datos para la fecha seleccionada.
      </div>
    </div>
  </div>
</template>

<style scoped>
.hourly-progress-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.hourly-progress-card__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

.progress-chart__canvas-wrapper {
  flex: 1 1 auto;
  min-height: 240px;
  position: relative;
}

.progress-chart__canvas {
  display: block;
  height: 100%;
  width: 100%;
}
</style>
